import { getSupabaseSession } from "./supabaseClient.js";
import {
  closeDetachedWindow,
  openDetachedWindow,
  updateDetachedWindowTitle,
} from "../utils/detachedWindow.js";
import {
  BROWSER_DOWNLOAD_DESTINATION_LABEL,
  SAVE_MODE_BROWSER_DOWNLOAD,
  writeBlobToSaveTarget,
  XLSX_MEDIA_TYPE,
} from "../utils/browserSaveTarget.js";
import {
  normalizeDispatchPeriod,
  readCurrentDispatchPeriod,
} from "../utils/dispatchDateUrls.js";

const PDF_MEDIA_TYPE = "application/pdf";
const MEUNERIE_REPORT_EMAIL_ENDPOINT = "/api/exports/meunerie/email";
const MEUNERIE_REPORT_OPTIONS_ENDPOINT = "/api/exports/meunerie/options";
const MEUNERIE_REPORT_EXPORT_ENDPOINT = "/api/exports/meunerie/pdf";
const MEUNERIE_MANUAL_TIMES_ENDPOINT = "/api/exports/meunerie/manual-times";
export const MEUNERIE_TIME_MODE_DISPATCH = "dispatch";
export const MEUNERIE_TIME_MODE_MANUAL = "manual";
const RESULTS_EXPORT_ENDPOINT = "/api/exports/results";
const ORDERS_ROUNDTRIP_ENDPOINT = "/api/exports/orders-roundtrip";
const SOLLIO_CONVERSION_ENDPOINT = "/api/import/sollio/convert";
const SOLLIO_ORDERS_FILE_NAME = "Orders_Sollio.xlsx";
const UNIAG_CONVERSION_ENDPOINT = "/api/import/uniag/convert";
const UNIAG_ORDERS_FILE_NAME = "Orders_Uniag.xlsx";
const TRUCK_PRELOAD_REPORT_EXPORT_ENDPOINT = "/api/exports/truck-preloads/pdf";
const TRUCK_SHEET_PDF_EXPORT_ENDPOINT = "/api/exports/truck-sheets/pdf";
const TRUCK_SHEET_REPORT_EMAIL_ENDPOINT = "/api/exports/truck-sheets/email";
const TRUCK_SHEET_EMAIL_CONTACTS_ENDPOINT = "/api/exports/truck-sheets/email-contacts";
const TRUCK_SHEET_EMAIL_TEMPLATE_ENDPOINT = "/api/exports/truck-sheets/email-template";
const DATA_MANAGER_ORDERS_PDF_EXPORT_ENDPOINT = "/api/data-manager/export/orders/pdf";
const REPORTS_ORDERS_PDF_EXPORT_ENDPOINT = "/api/exports/orders/pdf";
export const TRUCK_SHEET_PDF_PREVIEW_LIFETIME_MS = 60000;

function isPreviewableBinaryMediaType(mediaType = "") {
  return typeof mediaType === "string" && mediaType.includes(PDF_MEDIA_TYPE);
}

export function resolvePreviewObjectUrl(objectUrl, mediaType = "") {
  if (!isPreviewableBinaryMediaType(mediaType)) {
    return objectUrl;
  }

  if (typeof objectUrl !== "string" || !objectUrl || objectUrl.includes("#")) {
    return objectUrl;
  }

  return `${objectUrl}#zoom=75`;
}

export const PREVIEW_WINDOW_DESTINATION_LABEL = "Fenêtre du navigateur";

function resolveWindowTonePalette(tone = "info") {
  if (tone === "error") {
    return {
      accent: "#a03a31",
      background: "rgba(254, 242, 242, 0.94)",
      border: "rgba(220, 38, 38, 0.16)",
      detail: "#7f1d1d",
    };
  }

  if (tone === "success") {
    return {
      accent: "#0f5c57",
      background: "rgba(240, 253, 250, 0.94)",
      border: "rgba(15, 118, 110, 0.16)",
      detail: "#134e4a",
    };
  }

  return {
    accent: "#495b79",
    background: "rgba(248, 250, 252, 0.96)",
    border: "rgba(148, 163, 184, 0.2)",
    detail: "#475569",
  };
}

function createWindowTextElement(targetDocument, tagName, text, style) {
  const element = targetDocument.createElement(tagName);
  element.textContent = text;
  Object.assign(element.style, style);
  return element;
}

function isDetachedWindowMountNode(mountNode, targetDocument) {
  return Boolean(
    mountNode
    && targetDocument
    && mountNode.nodeType === 1
    && mountNode.ownerDocument === targetDocument,
  );
}

function renderExportDocumentWindowState(documentWindow, {
  detail = "",
  eyebrow = "Export",
  message = "",
  title = "",
  tone = "info",
} = {}) {
  const mountNode = documentWindow?.mountNode;
  const targetDocument = documentWindow?.popupWindow?.document;
  if (!isDetachedWindowMountNode(mountNode, targetDocument)) {
    return;
  }

  const palette = resolveWindowTonePalette(tone);
  Object.assign(mountNode.style, {
    alignItems: "center",
    display: "grid",
    minHeight: "100vh",
    padding: "32px",
  });

  const panel = targetDocument.createElement("section");
  Object.assign(panel.style, {
    background: palette.background,
    border: `1px solid ${palette.border}`,
    borderRadius: "24px",
    boxShadow: "0 28px 80px rgba(15, 23, 42, 0.08)",
    display: "grid",
    gap: "12px",
    maxWidth: "720px",
    padding: "28px 30px",
    width: "min(100%, 720px)",
  });

  panel.appendChild(createWindowTextElement(targetDocument, "p", eyebrow, {
    color: palette.accent,
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: "12px",
    fontWeight: "800",
    letterSpacing: "0.16em",
    margin: "0",
    textTransform: "uppercase",
  }));
  panel.appendChild(createWindowTextElement(targetDocument, "h1", title, {
    color: "#20323d",
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: "34px",
    fontWeight: "800",
    letterSpacing: "-0.03em",
    lineHeight: "1.02",
    margin: "0",
  }));
  panel.appendChild(createWindowTextElement(targetDocument, "p", message, {
    color: "#475569",
    fontFamily: '"Segoe UI", sans-serif',
    fontSize: "16px",
    lineHeight: "1.5",
    margin: "0",
  }));

  if (detail) {
    panel.appendChild(createWindowTextElement(targetDocument, "p", detail, {
      color: palette.detail,
      fontFamily: '"Segoe UI", sans-serif',
      fontSize: "14px",
      lineHeight: "1.5",
      margin: "0",
    }));
  }

  mountNode.replaceChildren(panel);
}

export function openExportDocumentWindow({
  detail = "",
  eyebrow = "Export",
  message = "Le document va s’ouvrir automatiquement dès qu’il sera prêt.",
  preferredHeight = 860,
  preferredWidth = 1160,
  title = "Préparation du document",
  windowName = "grain-export-document",
} = {}) {
  if (typeof window === "undefined") {
    return null;
  }

  const documentWindow = openDetachedWindow({
    height: preferredHeight,
    name: windowName,
    title,
    width: preferredWidth,
  });
  if (!documentWindow) {
    return null;
  }

  renderExportDocumentWindowState(documentWindow, {
    detail,
    eyebrow,
    message,
    title,
    tone: "info",
  });
  return documentWindow;
}

export function closeExportDocumentWindow(documentWindow) {
  closeDetachedWindow(documentWindow);
}

export function markExportDocumentWindowFailed(documentWindow, {
  detail = "",
  eyebrow = "Export",
  message = "Le document n’a pas pu être généré.",
  title = "Export interrompu",
} = {}) {
  if (!documentWindow) {
    return;
  }

  updateDetachedWindowTitle(documentWindow, title);
  renderExportDocumentWindowState(documentWindow, {
    detail,
    eyebrow,
    message,
    title,
    tone: "error",
  });
}

function markExportDocumentWindowDownloaded(documentWindow, {
  detail = "",
  eyebrow = "Export",
  message = "Le document a été généré et téléchargé.",
  title = "Document prêt",
} = {}) {
  if (!documentWindow) {
    return;
  }

  updateDetachedWindowTitle(documentWindow, title);
  renderExportDocumentWindowState(documentWindow, {
    detail,
    eyebrow,
    message,
    title,
    tone: "success",
  });
  documentWindow.popupWindow?.focus?.();
}

class ExportApiError extends Error {
  constructor(message, { code = "", payload = null, status = 0 } = {}) {
    super(message);
    this.name = "ExportApiError";
    this.code = code;
    this.payload = payload;
    this.status = status;
  }
}

function getDefaultFetch() {
  if (typeof globalThis.fetch !== "function") {
    throw new ExportApiError("Export indisponible : fetch n'est pas supporté.", {
      code: "network_error",
    });
  }

  return globalThis.fetch.bind(globalThis);
}

async function resolveAccessToken() {
  const { error, session } = await getSupabaseSession();
  if (error || !session?.access_token) {
    throw new ExportApiError("Votre session ne permet pas de lancer cet export.", {
      code: "permission_denied",
      status: 403,
    });
  }

  return session.access_token;
}

async function parseJsonSafely(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function buildExportApiError(response, payload) {
  const payloadMessage = typeof payload?.error === "string" && payload.error.trim()
    ? payload.error.trim()
    : typeof payload?.message === "string" && payload.message.trim()
      ? payload.message.trim()
      : typeof payload?.detail === "string" && payload.detail.trim()
        ? payload.detail.trim()
        : Array.isArray(payload?.detail) && payload.detail.length > 0
          ? (() => {
            const firstDetail = payload.detail[0];
            if (typeof firstDetail === "string" && firstDetail.trim()) {
              return firstDetail.trim();
            }
            if (typeof firstDetail?.msg === "string" && firstDetail.msg.trim()) {
              return firstDetail.msg.trim();
            }
            return `Erreur ${response.status}`;
          })()
      : `Erreur ${response.status}`;

  return new ExportApiError(payloadMessage, {
    code: typeof payload?.code === "string" ? payload.code.trim() : "",
    payload,
    status: response.status,
  });
}

function resolveFileNameFromContentDisposition(contentDisposition, fallbackFileName) {
  if (typeof contentDisposition === "string" && contentDisposition.trim()) {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(utf8Match[1]);
      } catch {
        return utf8Match[1];
      }
    }

    const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    if (asciiMatch?.[1]) {
      return asciiMatch[1];
    }
  }

  return fallbackFileName;
}

export function scheduleTruckSheetPdfPreviewCleanup(documentWindow, objectUrl) {
  if (
    typeof window === "undefined"
    || !documentWindow?.popupWindow
    || typeof documentWindow.popupWindow.close !== "function"
    || typeof objectUrl !== "string"
    || !objectUrl
  ) {
    return null;
  }

  return window.setTimeout(() => {
    try {
      closeExportDocumentWindow(documentWindow);
    } finally {
      window.URL.revokeObjectURL(objectUrl);
    }
  }, TRUCK_SHEET_PDF_PREVIEW_LIFETIME_MS);
}

function triggerBrowserDownload(blob, fileName, {
  autoCloseTruckSheetPreview = false,
  documentWindow = null,
  mediaType = "",
} = {}) {
  const objectUrl = window.URL.createObjectURL(blob);
  const previewUrl = resolvePreviewObjectUrl(objectUrl, mediaType);
  const canPreviewInDocumentWindow = Boolean(
    documentWindow?.popupWindow
      && !documentWindow.popupWindow.closed
      && isPreviewableBinaryMediaType(mediaType),
  );

  if (canPreviewInDocumentWindow) {
    updateDetachedWindowTitle(documentWindow, fileName);
    documentWindow.popupWindow.location.replace(previewUrl);
    documentWindow.popupWindow.focus?.();
    if (autoCloseTruckSheetPreview) {
      scheduleTruckSheetPdfPreviewCleanup(documentWindow, objectUrl);
    } else {
      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 300000);
    }

    return {
      destinationLabel: PREVIEW_WINDOW_DESTINATION_LABEL,
      fileName,
      openedInWindow: true,
      saveMode: SAVE_MODE_BROWSER_DOWNLOAD,
    };
  }

  if (documentWindow?.popupWindow && !documentWindow.popupWindow.closed) {
    markExportDocumentWindowDownloaded(documentWindow, {
      detail: "L’aperçu direct dans le navigateur n’est pas disponible pour ce format. Ouvrez le fichier depuis Téléchargements.",
      message: `Le fichier ${fileName} a été généré et le téléchargement a été lancé automatiquement.`,
      title: fileName,
    });
  }

  const anchor = window.document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  window.document.body.append(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => {
    window.URL.revokeObjectURL(objectUrl);
  }, isPreviewableBinaryMediaType(mediaType) ? 300000 : 120000);

  return {
    destinationLabel: BROWSER_DOWNLOAD_DESTINATION_LABEL,
    fileName,
    openedInWindow: false,
    saveMode: SAVE_MODE_BROWSER_DOWNLOAD,
  };
}

async function saveBinaryBlob(blob, fileName, {
  autoCloseTruckSheetPreview = false,
  documentWindow = null,
  mediaType = "",
  saveTarget = null,
} = {}) {
  if (saveTarget?.handle) {
    try {
      const savedFile = await writeBlobToSaveTarget(blob, saveTarget);
      if (savedFile) {
        closeExportDocumentWindow(documentWindow);
        return savedFile;
      }
    } catch (error) {
      if (error?.code === "save_cancelled") {
        throw new ExportApiError("Enregistrement annulé.", {
          code: "save_cancelled",
        });
      }
    }
  }

  return triggerBrowserDownload(blob, fileName, {
    autoCloseTruckSheetPreview,
    documentWindow,
    mediaType,
  });
}

async function downloadBinaryResponse(response, fallbackFileName, {
  autoCloseTruckSheetPreview = false,
  documentWindow = null,
  saveTarget = null,
} = {}) {
  const blob = await response.blob();
  const fileName = resolveFileNameFromContentDisposition(
    response.headers.get("content-disposition"),
    fallbackFileName,
  );
  const mediaType = response.headers.get("content-type") || blob.type || "";

  return saveBinaryBlob(blob, fileName, {
    autoCloseTruckSheetPreview,
    documentWindow,
    mediaType,
    saveTarget,
  });
}

async function performAuthorizedExportRequest(url, {
  body,
  fetchImpl = getDefaultFetch(),
  headers = {},
  method = "GET",
  signal,
} = {}) {
  const accessToken = await resolveAccessToken();
  return fetchImpl(url, {
    method,
    cache: "no-store",
    headers: {
      Accept: "application/json, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/pdf",
      Authorization: `Bearer ${accessToken}`,
      ...headers,
    },
    body,
    signal,
  });
}

function appendDispatchDate(url, dispatchDate = "", dispatchPeriod = "day") {
  if (typeof url !== "string" || !url) {
    return url;
  }

  const normalizedDispatchDate = typeof dispatchDate === "string"
    ? dispatchDate.trim()
    : "";
  const normalizedDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod);

  if (!normalizedDispatchDate && normalizedDispatchPeriod !== "evening") {
    return url;
  }

  if (typeof window === "undefined") {
    const queryItems = [];
    if (normalizedDispatchDate) {
      queryItems.push(`dispatch_date=${encodeURIComponent(normalizedDispatchDate)}`);
    }
    if (normalizedDispatchPeriod === "evening") {
      queryItems.push("dispatch_period=evening");
    }
    const separator = url.includes("?") ? "&" : "?";
    return queryItems.length ? `${url}${separator}${queryItems.join("&")}` : url;
  }

  const resolvedUrl = new URL(url, window.location.origin);
  if (normalizedDispatchDate && !resolvedUrl.searchParams.get("dispatch_date")) {
    resolvedUrl.searchParams.set("dispatch_date", normalizedDispatchDate);
  }
  if (normalizedDispatchPeriod === "evening") {
    resolvedUrl.searchParams.set("dispatch_period", "evening");
  } else {
    resolvedUrl.searchParams.delete("dispatch_period");
  }
  return `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`;
}

function appendCurrentDispatchDate(url, dispatchPeriod = readCurrentDispatchPeriod()) {
  if (typeof window === "undefined") {
    return appendDispatchDate(url, "", dispatchPeriod);
  }

  const currentSearchParams = new URLSearchParams(window.location.search || "");
  const dispatchDate = typeof currentSearchParams.get("dispatch_date") === "string"
    ? currentSearchParams.get("dispatch_date").trim()
    : "";

  return appendDispatchDate(url, dispatchDate, dispatchPeriod);
}

export function isExportApiError(error) {
  return error instanceof ExportApiError;
}

export async function downloadOrdersRoundtrip({ documentWindow, fetchImpl, saveTarget, signal } = {}) {
  const response = await performAuthorizedExportRequest(ORDERS_ROUNDTRIP_ENDPOINT, {
    fetchImpl,
    signal,
  });

  if (!response.ok) {
    throw buildExportApiError(response, await parseJsonSafely(response));
  }

  return downloadBinaryResponse(response, "orders.xlsx", {
    documentWindow,
    saveTarget,
  });
}

export async function loadMeunerieReportOptions({ fetchImpl, signal } = {}) {
  const response = await performAuthorizedExportRequest(MEUNERIE_REPORT_OPTIONS_ENDPOINT, {
    fetchImpl,
    signal,
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return Array.isArray(payload?.items) ? payload.items : [];
}

function normalizeMeunerieTimeMode(timeMode) {
  return timeMode === MEUNERIE_TIME_MODE_MANUAL ? MEUNERIE_TIME_MODE_MANUAL : MEUNERIE_TIME_MODE_DISPATCH;
}

function buildMeunerieManualTimesUrl(dispatchDate = "", query = "") {
  const url = query ? `${MEUNERIE_MANUAL_TIMES_ENDPOINT}?${query}` : MEUNERIE_MANUAL_TIMES_ENDPOINT;
  return dispatchDate ? appendDispatchDate(url, dispatchDate) : appendCurrentDispatchDate(url);
}

export async function loadMeunerieManualTimes({
  dispatchDate = "",
  fetchImpl,
  meunerieLookupKey = "",
  signal,
} = {}) {
  const normalizedMeunerieLookupKey = typeof meunerieLookupKey === "string" ? meunerieLookupKey.trim() : "";
  const query = normalizedMeunerieLookupKey
    ? `meunerie_lookup_key=${encodeURIComponent(normalizedMeunerieLookupKey)}`
    : "";
  const response = await performAuthorizedExportRequest(buildMeunerieManualTimesUrl(dispatchDate, query), {
    fetchImpl,
    signal,
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }
  return {
    documents: Array.isArray(payload?.documents) ? payload.documents : [],
    version: Number.isInteger(payload?.version) ? payload.version : 0,
  };
}

export async function saveMeunerieManualTimes({
  dispatchDate = "",
  fetchImpl,
  signal,
  times = {},
  version = 0,
} = {}) {
  const response = await performAuthorizedExportRequest(buildMeunerieManualTimesUrl(dispatchDate), {
    body: JSON.stringify({ times, version }),
    fetchImpl,
    headers: {
      "Content-Type": "application/json",
    },
    method: "PUT",
    signal,
  });
  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }
  return {
    times: payload?.times && typeof payload.times === "object" ? payload.times : {},
    version: Number.isInteger(payload?.version) ? payload.version : 0,
  };
}

export async function downloadMeunerieReport({
  dispatchDate = "",
  documentWindow,
  fetchImpl,
  meunerieLookupKey = "",
  saveTarget,
  signal,
  timeMode = MEUNERIE_TIME_MODE_DISPATCH,
} = {}) {
  const normalizedMeunerieLookupKey = typeof meunerieLookupKey === "string"
    ? meunerieLookupKey.trim()
    : "";

  if (!normalizedMeunerieLookupKey) {
    throw new ExportApiError("Sélectionnez une meunerie avant de générer le rapport.", {
      code: "missing_meunerie",
      status: 400,
    });
  }

  const response = await performAuthorizedExportRequest(
    dispatchDate
      ? appendDispatchDate(MEUNERIE_REPORT_EXPORT_ENDPOINT, dispatchDate)
      : appendCurrentDispatchDate(MEUNERIE_REPORT_EXPORT_ENDPOINT),
    {
    fetchImpl,
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    body: JSON.stringify({
      meunerie_lookup_key: normalizedMeunerieLookupKey,
      time_mode: normalizeMeunerieTimeMode(timeMode),
    }),
    signal,
    },
  );

  if (!response.ok) {
    throw buildExportApiError(response, await parseJsonSafely(response));
  }

  return downloadBinaryResponse(response, "grain_meunerie.pdf", {
    documentWindow,
    saveTarget,
  });
}

export async function downloadDispatchGanttReport({
  dispatchDate = "", dispatchPeriod = "day", colorIntensityPercent = 0,
  documentWindow, fetchImpl, signal,
} = {}) {
  const response = await performAuthorizedExportRequest(
    appendDispatchDate("/api/exports/dispatch-gantt/pdf", dispatchDate, dispatchPeriod),
    { method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ color_intensity_percent: colorIntensityPercent }), fetchImpl, signal },
  );
  if (!response.ok) throw buildExportApiError(response, await parseJsonSafely(response));
  return downloadBinaryResponse(response, "dispatch_visuel.pdf", { documentWindow });
}

export async function downloadGeotabOrdersReport({ dispatchDate = "", dateFrom = dispatchDate, dateTo = dateFrom, dispatchPeriod = "day", documentWindow, fetchImpl, signal } = {}) {
  const response = await performAuthorizedExportRequest(
    `/api/reports/geotab-orders/pdf?date_from=${encodeURIComponent(dateFrom)}&date_to=${encodeURIComponent(dateTo)}&period=${dispatchPeriod === "evening" ? "evening" : "day"}`,
    { fetchImpl, signal },
  );
  if (!response.ok) throw buildExportApiError(response, await parseJsonSafely(response));
  return downloadBinaryResponse(response, "comparatif_geotab.pdf", { documentWindow });
}

export async function downloadTruckPreloadReport({
  dispatchDate = "",
  documentWindow,
  employeeId = "",
  fetchImpl,
  notes = "",
  saveTarget,
  showCompartments = true,
  signal,
  truckIds = [],
} = {}) {
  const normalizedTruckIds = Array.isArray(truckIds)
    ? truckIds.map((truckId) => (typeof truckId === "string" ? truckId.trim() : "")).filter(Boolean)
    : [];
  const response = await performAuthorizedExportRequest(
    dispatchDate
      ? appendDispatchDate(TRUCK_PRELOAD_REPORT_EXPORT_ENDPOINT, dispatchDate)
      : appendCurrentDispatchDate(TRUCK_PRELOAD_REPORT_EXPORT_ENDPOINT),
    {
      body: JSON.stringify({
        employee_id: typeof employeeId === "string" ? employeeId.trim() : "",
        notes: typeof notes === "string" ? notes : "",
        show_compartments: showCompartments !== false,
        truck_ids: normalizedTruckIds,
      }),
      fetchImpl,
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      signal,
    },
  );

  if (!response.ok) {
    throw buildExportApiError(response, await parseJsonSafely(response));
  }

  return downloadBinaryResponse(response, "grain_truck_preloads.pdf", {
    documentWindow,
    saveTarget,
  });
}

export async function sendMeunerieReportEmail({
  dispatchDate = "",
  fetchImpl,
  meunerieLookupKey = "",
  recipientEmail = "",
  signal,
  subject = "",
  timeMode = MEUNERIE_TIME_MODE_DISPATCH,
} = {}) {
  const normalizedMeunerieLookupKey = typeof meunerieLookupKey === "string"
    ? meunerieLookupKey.trim()
    : "";
  const normalizedRecipientEmail = typeof recipientEmail === "string"
    ? recipientEmail.trim()
    : "";
  const normalizedSubject = typeof subject === "string"
    ? subject.trim()
    : "";

  if (!normalizedMeunerieLookupKey) {
    throw new ExportApiError("Sélectionnez une meunerie avant d'envoyer le rapport.", {
      code: "missing_meunerie",
      status: 400,
    });
  }

  if (!normalizedRecipientEmail) {
    throw new ExportApiError("Saisissez une adresse courriel destinataire.", {
      code: "missing_recipient_email",
      status: 400,
    });
  }

  if (!normalizedSubject) {
    throw new ExportApiError("Saisissez un sujet avant d'envoyer le rapport.", {
      code: "missing_subject",
      status: 400,
    });
  }

  const response = await performAuthorizedExportRequest(
    dispatchDate
      ? appendDispatchDate(MEUNERIE_REPORT_EMAIL_ENDPOINT, dispatchDate)
      : appendCurrentDispatchDate(MEUNERIE_REPORT_EMAIL_ENDPOINT),
    {
      body: JSON.stringify({
        meunerie_lookup_key: normalizedMeunerieLookupKey,
        recipient_email: normalizedRecipientEmail,
        subject: normalizedSubject,
        time_mode: normalizeMeunerieTimeMode(timeMode),
      }),
      fetchImpl,
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      signal,
    },
  );
  const payload = await parseJsonSafely(response);

  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return payload && typeof payload === "object" ? payload : {};
}

export async function loadTruckSheetEmailContacts({ dispatchDate = "", fetchImpl, signal } = {}) {
  const url = `${TRUCK_SHEET_EMAIL_CONTACTS_ENDPOINT}?${new URLSearchParams({ dispatch_date: dispatchDate })}`;
  const response = await performAuthorizedExportRequest(url, {
    fetchImpl,
    signal,
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return Array.isArray(payload?.items) ? payload.items : [];
}

export async function loadTruckSheetEmailTemplate({ fetchImpl, signal } = {}) {
  const response = await performAuthorizedExportRequest(TRUCK_SHEET_EMAIL_TEMPLATE_ENDPOINT, {
    fetchImpl,
    signal,
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return payload && typeof payload === "object" ? payload : {};
}

export async function saveTruckSheetEmailTemplate({
  fetchImpl,
  messageTemplate = "",
  signal,
  subjectTemplate = "",
} = {}) {
  const response = await performAuthorizedExportRequest(TRUCK_SHEET_EMAIL_TEMPLATE_ENDPOINT, {
    body: JSON.stringify({
      message_template: typeof messageTemplate === "string" ? messageTemplate : "",
      subject_template: typeof subjectTemplate === "string" ? subjectTemplate : "",
    }),
    fetchImpl,
    headers: {
      "Content-Type": "application/json",
    },
    method: "PUT",
    signal,
  });

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return payload && typeof payload === "object" ? payload : {};
}

export async function sendTruckSheetReportEmail({
  dispatchDate = "",
  dispatchPeriod = readCurrentDispatchPeriod(),
  fetchImpl,
  fitOnePage = false,
  message = "",
  notes = "",
  onCallDispatcher = "",
  orientation = "portrait",
  recipientEmail = "",
  showCompartments = true,
  showMap = true,
  signal,
  subject = "",
  truckId = "",
} = {}) {
  const normalizedTruckId = typeof truckId === "string" ? truckId.trim() : "";
  const normalizedRecipientEmail = typeof recipientEmail === "string"
    ? recipientEmail.trim()
    : "";
  const normalizedSubject = typeof subject === "string"
    ? subject.trim()
    : "";
  const normalizedMessage = typeof message === "string"
    ? message.trim()
    : "";
  const normalizedNotes = typeof notes === "string" ? notes.trim() : "";
  const normalizedDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod);
  const normalizedOrientation = orientation === "landscape" ? "landscape" : "portrait";
  const normalizedFitOnePage = Boolean(fitOnePage);

  if (!normalizedTruckId) {
    throw new ExportApiError("Sélectionnez un camion avant d'envoyer l'horaire chauffeur.", {
      code: "missing_truck",
      status: 400,
    });
  }

  if (!normalizedRecipientEmail) {
    throw new ExportApiError("Saisissez une adresse courriel destinataire.", {
      code: "missing_recipient_email",
      status: 400,
    });
  }

  if (!normalizedSubject) {
    throw new ExportApiError("Saisissez un sujet avant d'envoyer l'horaire chauffeur.", {
      code: "missing_subject",
      status: 400,
    });
  }

  const response = await performAuthorizedExportRequest(
    dispatchDate
      ? appendDispatchDate(TRUCK_SHEET_REPORT_EMAIL_ENDPOINT, dispatchDate, normalizedDispatchPeriod)
      : appendCurrentDispatchDate(TRUCK_SHEET_REPORT_EMAIL_ENDPOINT, normalizedDispatchPeriod),
    {
      body: JSON.stringify({
        dispatch_period: normalizedDispatchPeriod,
        fit_one_page: normalizedFitOnePage,
        message: normalizedMessage,
        notes: normalizedNotes,
        on_call_dispatcher: typeof onCallDispatcher === "string" ? onCallDispatcher.trim() : "",
        orientation: normalizedOrientation,
        recipient_email: normalizedRecipientEmail,
        show_compartments: showCompartments !== false,
        show_map: showMap !== false,
        subject: normalizedSubject,
        truck_id: normalizedTruckId,
      }),
      fetchImpl,
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      signal,
    },
  );
  const payload = await parseJsonSafely(response);

  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return payload && typeof payload === "object" ? payload : {};
}

export async function convertSollioToOrders({
  deliveryAddressSelections,
  file,
  fetchImpl,
  saveTarget,
  signal,
} = {}) {
  if (!(file instanceof Blob)) {
    throw new ExportApiError("Sélectionnez un fichier Sollio avant de lancer la conversion.", {
      code: "missing_file",
      status: 400,
    });
  }

  const formData = new FormData();
  formData.append("sollio", file, typeof file.name === "string" && file.name.trim() ? file.name : "sollio.xlsx");
  if (Array.isArray(deliveryAddressSelections)) {
    formData.append("delivery_address_selections", JSON.stringify(deliveryAddressSelections));
  }
  const response = await performAuthorizedExportRequest(SOLLIO_CONVERSION_ENDPOINT, {
    body: formData,
    fetchImpl,
    method: "POST",
    signal,
  });

  if (!response.ok) {
    throw buildExportApiError(response, await parseJsonSafely(response));
  }

	const reusedSelectionCount = Number.parseInt(response.headers.get("x-sollio-reused-selection-count") || "0", 10);
	const savedSelectionCount = Number.parseInt(response.headers.get("x-sollio-saved-selection-count") || "0", 10);
	const download = await downloadBinaryResponse(response, SOLLIO_ORDERS_FILE_NAME, {
    saveTarget,
  });
	return {
		...download,
		reusedSelectionCount: Number.isFinite(reusedSelectionCount) ? reusedSelectionCount : 0,
		savedSelectionCount: Number.isFinite(savedSelectionCount) ? savedSelectionCount : 0,
	};
}

export async function convertUniagToOrders({ file, fetchImpl, saveTarget, signal } = {}) {
  if (!(file instanceof Blob)) {
    throw new ExportApiError("Sélectionnez un fichier Uniag avant de lancer la conversion.", {
      code: "missing_file",
      status: 400,
    });
  }

  const formData = new FormData();
  formData.append("uniag", file, typeof file.name === "string" && file.name.trim() ? file.name : "uniag.xlsx");
  const response = await performAuthorizedExportRequest(UNIAG_CONVERSION_ENDPOINT, {
    body: formData,
    fetchImpl,
    method: "POST",
    signal,
  });

  if (!response.ok) {
    throw buildExportApiError(response, await parseJsonSafely(response));
  }

  return downloadBinaryResponse(response, UNIAG_ORDERS_FILE_NAME, {
    saveTarget,
  });
}

export async function launchResultsExport({ documentWindow, fetchImpl, saveTarget, signal } = {}) {
  const response = await performAuthorizedExportRequest(appendCurrentDispatchDate(RESULTS_EXPORT_ENDPOINT), {
    fetchImpl,
    method: "POST",
    signal,
  });
  const contentType = response.headers.get("content-type") || "";

  if (response.ok && contentType.includes(XLSX_MEDIA_TYPE)) {
    const download = await downloadBinaryResponse(response, "grain_results_export.xlsx", {
      documentWindow,
      saveTarget,
    });
    return {
      download,
      mode: "sync",
    };
  }

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return {
    mode: "async",
    payload,
  };
}

export async function getResultsExportStatus(exportId, { fetchImpl, signal } = {}) {
  const response = await performAuthorizedExportRequest(appendCurrentDispatchDate(`${RESULTS_EXPORT_ENDPOINT}/${encodeURIComponent(exportId)}/status`), {
    fetchImpl,
    signal,
  });
  const payload = await parseJsonSafely(response);

  if (response.status === 410 && payload?.status === "expired") {
    return payload || {};
  }

  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return payload || {};
}

export async function getResultsExportHistory({ fetchImpl, signal } = {}) {
  const response = await performAuthorizedExportRequest(appendCurrentDispatchDate(`${RESULTS_EXPORT_ENDPOINT}/history`), {
    fetchImpl,
    signal,
  });
  const payload = await parseJsonSafely(response);

  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  if (!Array.isArray(payload?.items)) {
    return { items: [] };
  }

  return payload;
}

export async function getReportsActionsHistory({ fetchImpl, mode = "preview", signal } = {}) {
  const normalizedMode = typeof mode === "string" && mode.trim().toLowerCase() === "full" ? "full" : "preview";
  const endpoint = `/api/exports/actions/history?mode=${encodeURIComponent(normalizedMode)}`;
  const response = await performAuthorizedExportRequest(appendCurrentDispatchDate(endpoint), {
    fetchImpl,
    signal,
  });
  const payload = await parseJsonSafely(response);

  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  if (!Array.isArray(payload?.items)) {
    return { items: [] };
  }

  return payload;
}

export async function downloadResultsExport(exportId, { documentWindow, fetchImpl, saveTarget, signal } = {}) {
  const response = await performAuthorizedExportRequest(appendCurrentDispatchDate(`${RESULTS_EXPORT_ENDPOINT}/${encodeURIComponent(exportId)}/download`), {
    fetchImpl,
    signal,
  });

  if (!response.ok) {
    throw buildExportApiError(response, await parseJsonSafely(response));
  }

  return downloadBinaryResponse(response, "grain_results_export.xlsx", {
    documentWindow,
    saveTarget,
  });
}

export async function downloadDataManagerOrdersPdf({
  documentWindow = null,
  exportViewModel = {},
  fetchImpl,
  saveTarget = null,
  signal,
} = {}) {
  return downloadOrdersPdfExport({
    documentWindow,
    endpoint: DATA_MANAGER_ORDERS_PDF_EXPORT_ENDPOINT,
    exportViewModel,
    fetchImpl,
    saveTarget,
    signal,
  });
}

export async function downloadReportsOrdersPdf({
  documentWindow = null,
  exportViewModel = {},
  fetchImpl,
  saveTarget = null,
  signal,
} = {}) {
  return downloadOrdersPdfExport({
    documentWindow,
    endpoint: REPORTS_ORDERS_PDF_EXPORT_ENDPOINT,
    exportViewModel,
    fetchImpl,
    saveTarget,
    signal,
  });
}

async function downloadOrdersPdfExport({
  documentWindow = null,
  endpoint,
  exportViewModel = {},
  fetchImpl,
  saveTarget = null,
  signal,
}) {
  const response = await performAuthorizedExportRequest(endpoint, {
    body: JSON.stringify(exportViewModel),
    fetchImpl,
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    signal,
  });

  if (!response.ok) {
    throw buildExportApiError(response, await parseJsonSafely(response));
  }

  return downloadBinaryResponse(response, "grain_commandes_data_manager.pdf", {
    documentWindow,
    saveTarget,
  });
}

export async function launchTruckSheetPdfExport({
  dispatchPeriod = readCurrentDispatchPeriod(),
  documentWindow,
  exportMode = "combined",
  fetchImpl,
  fitOnePage = false,
  notes = "",
  onCallDispatcher = "",
  orientation = "portrait",
  showCompartments = true,
  showMap = true,
  signal,
  truckIds = [],
} = {}) {
  const normalizedDispatchPeriod = normalizeDispatchPeriod(dispatchPeriod);
  const normalizedOrientation = orientation === "landscape" ? "landscape" : "portrait";
  const response = await performAuthorizedExportRequest(appendCurrentDispatchDate(TRUCK_SHEET_PDF_EXPORT_ENDPOINT, normalizedDispatchPeriod), {
    body: JSON.stringify({
      dispatch_period: normalizedDispatchPeriod,
      export_mode: exportMode,
      fit_one_page: Boolean(fitOnePage),
      notes: typeof notes === "string" ? notes.trim() : "",
      on_call_dispatcher: typeof onCallDispatcher === "string" ? onCallDispatcher.trim() : "",
      orientation: normalizedOrientation,
      show_compartments: showCompartments !== false,
      show_map: showMap !== false,
      truck_ids: Array.isArray(truckIds)
        ? truckIds.filter((truckId) => typeof truckId === "string" && truckId.trim()).map((truckId) => truckId.trim())
        : [],
    }),
    fetchImpl,
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    signal,
  });
  const contentType = response.headers.get("content-type") || "";

  if (response.ok && contentType.includes(PDF_MEDIA_TYPE)) {
    const warning = decodeURIComponent(response.headers.get("x-truck-sheet-warning") || "");
    const download = await downloadBinaryResponse(response, "grain_truck_sheet.pdf", {
      autoCloseTruckSheetPreview: true,
      documentWindow,
    });
    return {
      download,
      mode: "sync",
      warning,
    };
  }

  const payload = await parseJsonSafely(response);
  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return {
    mode: "async",
    payload,
  };
}

export async function getTruckSheetPdfExportStatus(exportId, { fetchImpl, signal } = {}) {
  const response = await performAuthorizedExportRequest(appendCurrentDispatchDate(`${TRUCK_SHEET_PDF_EXPORT_ENDPOINT}/${encodeURIComponent(exportId)}/status`), {
    fetchImpl,
    signal,
  });
  const payload = await parseJsonSafely(response);

  if (response.status === 410 && payload?.status === "expired") {
    return payload || {};
  }

  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  return payload || {};
}

export async function getTruckSheetPdfExportHistory({ fetchImpl, signal } = {}) {
  const response = await performAuthorizedExportRequest(appendCurrentDispatchDate(`${TRUCK_SHEET_PDF_EXPORT_ENDPOINT}/history`), {
    fetchImpl,
    signal,
  });
  const payload = await parseJsonSafely(response);

  if (!response.ok) {
    throw buildExportApiError(response, payload);
  }

  if (!Array.isArray(payload?.items)) {
    return { items: [] };
  }

  return payload;
}

export async function downloadTruckSheetPdfExport(exportId, { documentWindow, fetchImpl, signal } = {}) {
  const response = await performAuthorizedExportRequest(appendCurrentDispatchDate(`${TRUCK_SHEET_PDF_EXPORT_ENDPOINT}/${encodeURIComponent(exportId)}/download`), {
    fetchImpl,
    signal,
  });

  if (!response.ok) {
    throw buildExportApiError(response, await parseJsonSafely(response));
  }

  return downloadBinaryResponse(response, "grain_truck_sheet.pdf", {
    autoCloseTruckSheetPreview: true,
    documentWindow,
  });
}
